import LanguageIcon from '@mui/icons-material/Language';
import { Checkbox, FormControlLabel, FormGroup, FormLabel, IconButton, Stack } from '@mui/material';
import ActionButton from 'common/components/ActionButton';
import CustomTextValidator from 'common/components/CustomTextValidators';
import YesNoDialog from 'common/components/YesNoDialog';
import useFlexBoxStyles from 'common/flexBox';
import { useFormFields } from 'libs/hooksLib';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ValidatorForm } from 'react-material-ui-form-validator';
import { useDispatch } from 'react-redux';
import { openNestedDialog } from 'redux/slices/nestedDialogSlice';
import IntlLabel from './FormI18nLabel';

const FormFormSettings = ({ fields, handleFieldChange, step, setStep, cancel, handleError }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { classes: flexBox, cx } = useFlexBoxStyles();
    const [openConfirmDeleteFields, setOpenConfirmDeleteFields] = useState(false);
    const deleteFieldsConfirmTitle = t('form.formForm.deleteFieldsConfirm');
    const deleteFieldsConfirmMessage = t('form.formForm.deleteMessageFields');

    const initialValues = useMemo(
        () => ({
            textActionEn: fields.textActionSubmitJoinForm
                ? fields.textActionSubmitJoinForm.find((item) => item.languageCode.toLowerCase() === 'en')?.value
                : '',
        }),
        [fields.textActionSubmitJoinForm],
    );
    const [fieldsSettings, handleFieldSettingsChange] = useFormFields(initialValues);

    const confirmDeleteFields = () => {
        handleFieldChange({
            target: {
                name: 'fields',
                value: [],
            },
        });
        setOpenConfirmDeleteFields(false);
    };

    const noDeleteFields = () => {
        setOpenConfirmDeleteFields(false);
    };

    const resetContext = useCallback(() => {
        handleFieldChange({
            target: {
                name: 'context',
                value: null,
            },
        });
        if (Array.isArray(fields.fields) && fields.fields.length > 0) {
            handleOpenDeleteFieldsDialog();
        }
    }, [fields.fields, handleFieldChange]);

    const handleSubmit = () => {
        const newTextAction = fields.textActionSubmitJoinForm
            .map((item) => {
                if (item.languageCode === 'en') {
                    return { languageCode: 'en', value: fieldsSettings.textActionEn };
                }
                return item;
            })
            .filter((item) => item.value !== '');

        handleFieldChange({
            target: {
                name: 'textActionSubmitJoinForm',
                value: newTextAction,
            },
        });
        setStep(step + 1);
    };

    const handleChangeCheck = (e) => {
        handleFieldChange({
            target: {
                name: e.target.name,
                value: e.target.checked,
            },
        });
    };

    const handleOpenDeleteFieldsDialog = () => {
        setOpenConfirmDeleteFields(true);
    };

    useEffect(() => {
        if (!fields.specifyContext && fields.context) {
            resetContext();
        }
    }, [fields.specifyContext, fields.context, resetContext]);

    const toggleChange = ({ value }, onSuccess) => {
        handleFieldChange({
            target: {
                name: 'textActionSubmitJoinForm',
                value: value,
            },
        });
        onSuccess();
    };

    return (
        <ValidatorForm
            onSubmit={handleSubmit}
            onError={handleError}
            className={cx(flexBox.flex, flexBox.gap2, flexBox.column, flexBox.overflowHidden)}
        >
            <Stack direction="column" spacing={2} className={cx(flexBox.overflowXHidden, flexBox.scrollbarNone)}>
                <FormGroup>
                    <FormLabel>{t('form.formForm.name')}</FormLabel>
                    <CustomTextValidator
                        name="name"
                        fullWidth={true}
                        variant="outlined"
                        size="small"
                        autoComplete="new-name"
                        value={fields.name}
                        onChange={handleFieldChange}
                        validators={['required']}
                        errorMessages={[t('app.common.required')]}
                    />
                </FormGroup>

                <FormGroup>
                    <FormLabel>{t('form.formForm.description')}</FormLabel>
                    <CustomTextValidator
                        name="description"
                        fullWidth={true}
                        variant="outlined"
                        size="small"
                        value={fields.description}
                        onChange={handleFieldChange}
                    />
                </FormGroup>

                <FormGroup>
                    <FormLabel>{t('form.formForm.textActionEn')}</FormLabel>
                    <CustomTextValidator
                        name="textActionEn"
                        fullWidth={true}
                        variant="outlined"
                        size="small"
                        autoComplete="new-text-action"
                        value={fieldsSettings.textActionEn}
                        onChange={handleFieldSettingsChange}
                        validators={['required']}
                        errorMessages={[t('app.common.required')]}
                        InputProps={{
                            endAdornment: (
                                <IconButton
                                    onClick={() => {
                                        dispatch(
                                            openNestedDialog(
                                                <IntlLabel
                                                    fieldLabels={fields.textActionSubmitJoinForm}
                                                    onSubmit={toggleChange}
                                                />,
                                            ),
                                        );
                                    }}
                                >
                                    <LanguageIcon />
                                </IconButton>
                            ),
                        }}
                    />
                </FormGroup>

                <FormGroup>
                    <FormControlLabel
                        control={
                            <Checkbox
                                className={cx(flexBox.padding0, flexBox.margin01)}
                                checked={fields.specifyContext}
                                onChange={handleChangeCheck}
                                name="specifyContext"
                                color="primary"
                            />
                        }
                        label={<FormLabel>{t('form.formForm.specifyContext')}</FormLabel>}
                    />
                </FormGroup>
            </Stack>

            <Stack
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                spacing={1}
                className={flexBox.paddingTop2}
            >
                <ActionButton onClick={cancel} color="default" fitWidth>
                    {t('app.common.cancel')}
                </ActionButton>

                <ActionButton color="primary" autoFocus fitWidth type={'submit'}>
                    {t('app.common.save')}
                </ActionButton>
            </Stack>
            <YesNoDialog
                openDialog={openConfirmDeleteFields}
                title={deleteFieldsConfirmTitle}
                messageToConfirm={deleteFieldsConfirmMessage}
                yesButtonCallback={() => {
                    confirmDeleteFields();
                }}
                noButtonCallback={() => {
                    noDeleteFields();
                }}
            />
        </ValidatorForm>
    );
};

export default FormFormSettings;
